import { AddressV2 } from "api/models";
import { formatAddressV2 } from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryAddress/helpers";

export type AddressSelectOption = ReturnType<typeof formatAddressV2SelectOption>;

export function formatAddressV2SelectOption(address: AddressV2) {
  const formatted = formatAddressV2(address);

  return {
    ...formatted,
    value: formatted.reference_number || String(formatted.id),
  };
}

/** Builds a select option from persisted form fields when the address is not in search results. */
export function buildSyntheticAddressOption(fields: {
  id?: number;
  name?: string;
  street_address?: string;
  street_address_line_two?: string;
  city_name?: string;
  state_name?: string;
  country_name?: string;
  zipcode?: string;
  reference_number: string;
  un_locode?: string | null;
  location_reference_number?: string;
  custom_address_reference?: string;
}): AddressSelectOption {
  return formatAddressV2SelectOption({
    id: fields.id ?? 0,
    name: fields.name ?? "",
    street_address: fields.street_address ?? "",
    street_address_line_two: fields.street_address_line_two ?? "",
    city_name: fields.city_name ?? "",
    state_name: fields.state_name ?? "",
    country_name: fields.country_name ?? "",
    zipcode: fields.zipcode ?? "",
    reference_number: fields.reference_number,
    un_locode: fields.un_locode ?? null,
    location_reference_number: fields.location_reference_number ?? "",
    custom_address_reference: fields.custom_address_reference ?? "",
  } as AddressV2);
}

function optionMatchesReference(option: AddressSelectOption, referenceNumber: string): boolean {
  return option.value === referenceNumber || option.reference_number === referenceNumber;
}

/** Ensures the currently selected address appears in options (react-select requires a matching option). */
export function buildAddressSelectOptions({
  mapped,
  addressReferenceNumber,
  fallbackOption,
}: {
  mapped: AddressSelectOption[];
  addressReferenceNumber?: string | null;
  fallbackOption?: AddressSelectOption | null;
}): AddressSelectOption[] {
  if (!addressReferenceNumber) {
    return mapped;
  }

  if (mapped.some((option) => optionMatchesReference(option, addressReferenceNumber))) {
    return mapped;
  }

  if (!fallbackOption || !optionMatchesReference(fallbackOption, addressReferenceNumber)) {
    return mapped;
  }

  return [
    fallbackOption,
    ...mapped.filter((option) => !optionMatchesReference(option, addressReferenceNumber)),
  ];
}
